import { supabase } from "../supabase";
import toast from "react-hot-toast";
import { generateUniqueFileName } from "../../utils/helpers";
import { PutObjectCommand, DeleteObjectCommand } from "@aws-sdk/client-s3";
import { s3Client, BUCKET_NAME } from "../aws/s3Config";

export const usePostsService = () => {
  const apiKey = process.env.N8N_WEBHOOK_KEY;
  // Upload image to AWS S3
  const uploadImage = async (file) => {
    try {
      const fileName = generateUniqueFileName(file.name);

      // Convert file to buffer
      const buffer = await file.arrayBuffer();

      // Upload to S3
      const uploadParams = {
        Bucket: BUCKET_NAME,
        Key: fileName,
        Body: new Uint8Array(buffer),
        ContentType: file.type,
      };

      const command = new PutObjectCommand(uploadParams);
      await s3Client.send(command);

      // Construct public URL
      const publicUrl = `https://${BUCKET_NAME}.s3.${process.env.REACT_APP_AWS_REGION}.amazonaws.com/${fileName}`;

      return {
        fileName,
        publicUrl,
      };
    } catch (error) {
      toast.error(`Image upload failed: ${error.message}`);
      throw error;
    }
  };

  // Delete image from AWS S3
  const deleteImage = async (fileName) => {
    try {
      const deleteParams = {
        Bucket: BUCKET_NAME,
        Key: fileName,
      };

      const command = new DeleteObjectCommand(deleteParams);
      await s3Client.send(command);
    } catch (error) {
      console.error("Error deleting image:", error);
    }
  };

 const addPost = async (postData) => {
  try {
    const { data: newPost, error } = await supabase
      .from("posts")
      .insert([postData])
      .select()
      .single();

    if (error) throw error;

    toast.success("Postimi u krijua me sukses!");

    // Send to proxy (no CORS issue)
    await fetch("http://localhost:5123/api/new-post", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        title: postData.title,
        slug: postData.slug,
        excerpt: newPost.excerpt,
        categories: newPost.categories,
        image_url: newPost.image_url,
        url: `https://fastidious-lamington-55c5c5.netlify.app/posts/${postData.slug}`,
      }),
    });

  } catch (error) {
    toast.error(`Gabim gjatë krijimit të postimit: ${error.message}`);
    throw error;
  }
};


  // Get all posts with filters
  const getPosts = async (
    page = 1,
    limit = 15,
    searchTerm = "",
    categoryFilter = ""
  ) => {
    const ITEMS_PER_PAGE = limit;
    const startIndex = (page - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE - 1;

    let query = supabase
      .from("posts")
      .select("*", { count: "exact", head: false })
      .order("created_at", { ascending: false })
      .range(startIndex, endIndex);

    // Server-side filtering (if supported by your schema)
    if (searchTerm) {
      query = query.ilike("title", `%${searchTerm}%`);
    }
    if (categoryFilter) {
      query = query.contains("categories", [categoryFilter]);
    }

    const { data, error, count } = await query;

    if (error) {
      console.error("Error fetching posts:", error);
      return { data: [], totalCount: 0 };
    }

    return {
      data: data ?? [],
      totalCount: count ?? 0,
    };
  };

  // Get single post
  const getPost = async (id) => {
    try {
      const { data, error } = await supabase
        .from("posts")
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw error;

      return data;
    } catch (error) {
      toast.error(`Error fetching post: ${error.message}`);
      throw error;
    }
  };

  // Update post
  const updatePost = async (id, updateData) => {
    try {
      const { data, error } = await supabase
        .from("posts")
        .update(updateData)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;

      toast.success("Post updated successfully!");
      return data;
    } catch (error) {
      toast.error(`Error updating post: ${error.message}`);
      throw error;
    }
  };

  // Delete post
  const deletePost = async (id, imageFileName) => {
    try {
      // Delete image if exists
      if (imageFileName) {
        await deleteImage(imageFileName);
      }

      const { error } = await supabase.from("posts").delete().eq("id", id);

      if (error) throw error;

      toast.success("Post deleted successfully!");
    } catch (error) {
      toast.error(`Error deleting post: ${error.message}`);
      throw error;
    }
  };

  return {
    uploadImage,
    deleteImage,
    addPost,
    getPosts,
    getPost,
    updatePost,
    deletePost,
  };
};
